import type { ChangeEvent } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { Schema } from "src/features/observationsAdmin/components/CreateNewObservationForm/schema";

interface useNewObservationFormHandlerOptions {
	setValue: UseFormSetValue<Schema>;
}

const useNewObservationFormHandler = ({
	setValue
}: useNewObservationFormHandlerOptions) => {
	const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		setValue("formName", e.target.value, { shouldValidate: true });
	};

	const onObservationTypeChange = (value: IObservationType) => {
		setValue("observationType", value, { shouldValidate: true });

		if (value !== "GREAT_CONVERSATION" && value !== "INDIVIDUAL") {
			setValue("limitToCurrentUser", false, { shouldValidate: true });
		}
	};

	const onCompletionSettingsChange = (value: string) => {
		if (value === "send") {
			return setValue("sendEmail", true, { shouldValidate: true });
		}

		setValue("sendEmail", false, { shouldValidate: true });

		setValue("observationRecipients", [], { shouldValidate: true });
		setValue("gradingRecipients", [], { shouldValidate: true });

		setValue("observationSpecificEmailAddress", "", { shouldValidate: true });
		setValue("gradingSpecificEmailAddress", "", { shouldValidate: true });
	};

	const onObservationRecipientChange = (value: (string | number)[]) => {
		if (typeof value === "number") return;
		// Appease typescript, value in checkbox could be string | number[]
		// In this context it will ALWAYS! be a string

		setValue("observationRecipients", value as EmailRecipient[], {
			shouldValidate: true
		});
	};

	const onGradingRecipientChange = (value: (string | number)[]) => {
		if (typeof value === "number") return;
		// Appease typescript, value in checkbox could be string | number[]
		// In this context it will ALWAYS! be a string

		setValue("gradingRecipients", value as EmailRecipient[], {
			shouldValidate: true
		});
	};

	const onObservationEmailChange = (value: string) => {
		setValue("observationSpecificEmailAddress", value, {
			shouldValidate: true
		});
	};

	const onGradingEmailChange = (value: string) => {
		setValue("gradingSpecificEmailAddress", value, { shouldValidate: true });
	};

	const onCompletionCustomEmailSettingsChange = (value: string) => {
		setValue("customEmail", value === "true", { shouldValidate: true });
	};

	const onCustomEmailTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setValue("customEmailTitle", e.target.value, { shouldValidate: true });
	};

	const onCustomEmailSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
		setValue("customEmailSubject", e.target.value, { shouldValidate: true });
	};

	const onCustomEmailBodyChange = (e: any) => {
		setValue("customEmailBody", e.target.value, { shouldValidate: true });
	};

	const onObserverAccessChange = (value: string) => {
		setValue("limitToCurrentUser", value === "true", { shouldValidate: true });
	};

	const onCategoryChange = (e: any) => {
		setValue("category", parseInt(e.target.value), { shouldValidate: true });
	};

	return {
		onNameChange,
		onObservationTypeChange,
		onCompletionSettingsChange,
		onObservationRecipientChange,
		onGradingRecipientChange,
		onObservationEmailChange,
		onGradingEmailChange,
		onCompletionCustomEmailSettingsChange,
		onCustomEmailTitleChange,
		onCustomEmailSubjectChange,
		onCustomEmailBodyChange,
		onObserverAccessChange,
		onCategoryChange
	};
};

export {
	useNewObservationFormHandler,
	type useNewObservationFormHandlerOptions
};
